import { useNotification } from '@hooks/useNotification';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function ReactQueryServerErrorNotifications() {
  const { t } = useTranslation();
  const notification = useNotification();
  const queryClient = useQueryClient();

  useEffect(() => {
    const shouldNotify = (error: unknown) => {
      const typedError = error as {
        status?: number | string;
        response?: { status?: number | string };
        name?: string;
        message?: string;
      };

      const rawStatus = typedError?.status ?? typedError?.response?.status;
      const status =
        typeof rawStatus === 'string' ? Number(rawStatus) : rawStatus;

      const isServerError =
        typeof status === 'number' && status >= 500 && status < 600;
      const isNetworkError =
        rawStatus == null &&
        (error instanceof TypeError ||
          typedError?.name === 'TypeError' ||
          /failed to fetch|network|load failed/i.test(
            typedError?.message || '',
          ));

      return isServerError || isNetworkError;
    };

    const unsubscribeQueries = queryClient
      .getQueryCache()
      .subscribe((event) => {
        if (event?.type !== 'updated') return;

        const query = event.query;

        if (query.state.status !== 'error') return;
        if (!shouldNotify(query.state.error)) return;

        notification.error(t('errorOccurred'));
      });

    const unsubscribeMutations = queryClient
      .getMutationCache()
      .subscribe((event) => {
        if (event?.type !== 'updated') return;

        const mutation = event.mutation;

        if (mutation.state.status !== 'error') return;
        if (!shouldNotify(mutation.state.error)) return;

        notification.error(t('errorOccurred'));
      });

    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [notification, queryClient, t]);

  return null;
}
